import { supabase } from "~/integrations/supabase/client";
import type {
  PickTask,
  PickTaskItem,
  PickingOrderItem,
  PickingOrdersByClient,
  StockAvailabilityResult,
} from "~/types/wms";

interface PickTaskWithDetails extends PickTask {
  sales_request?: {
    id: string;
    client?: { name: string };
    delivery_date?: string;
  };
  items?: PickTaskItemWithDetails[];
}

interface PickTaskItemWithDetails extends PickTaskItem {
  product?: { id: string; short_name: string };
  source_position?: { id: string; full_code: string };
  inventory?: {
    id: string;
    box_count: number;
    reserved_boxes: number;
  };
}

interface GetWorkerPickTasksResult {
  myTasks: PickTaskWithDetails[];
  pending: PickTaskWithDetails[];
  totalPending: number;
}

export const pickingService = {
  /**
   * Get pick tasks for worker (pending + assigned to me)
   */
  async getWorkerPickTasks(
    userId: string,
    options?: { page: number; pageSize: number }
  ): Promise<GetWorkerPickTasksResult> {
    const { page = 1, pageSize = 10 } = options || {};

    // My active tasks
    const { data: myTasks } = await supabase
      .from("wms_pick_tasks")
      .select(
        `
        *,
        sales_request:sales_request(
          id,
          client:clients(name),
          delivery_date
        ),
        items:wms_pick_task_items(
          id, product_id, requested_quantity, picked_quantity, status,
          product:products(id, short_name),
          source_position:wms_positions(id, full_code)
        )
      `
      )
      .eq("assigned_to", userId)
      .in("status", ["assigned", "in_progress"])
      .order("priority", { ascending: true });

    // Pending tasks (paginated)
    const from = (page - 1) * pageSize;
    const { data: pending, count } = await supabase
      .from("wms_pick_tasks")
      .select(
        `
        *,
        sales_request:sales_request(
          id,
          client:clients(name),
          delivery_date
        )
      `,
        { count: "exact" }
      )
      .eq("status", "pending")
      .order("priority", { ascending: true })
      .range(from, from + pageSize - 1);

    return {
      myTasks: (myTasks as PickTaskWithDetails[]) || [],
      pending: (pending as PickTaskWithDetails[]) || [],
      totalPending: count || 0,
    };
  },

  /**
   * Claim a pick task
   */
  async claimPickTask(
    taskId: string,
    userId: string
  ): Promise<PickTaskWithDetails> {
    const { data, error } = await supabase
      .from("wms_pick_tasks")
      .update({
        assigned_to: userId,
        assigned_at: new Date().toISOString(),
        status: "assigned",
      })
      .eq("id", taskId)
      .eq("status", "pending")
      .select(
        `
        *,
        sales_request:sales_request(
          id,
          client:clients(name),
          delivery_date
        ),
        items:wms_pick_task_items(
          id, product_id, requested_quantity, picked_quantity, status, pick_sequence,
          product:products(id, short_name),
          source_position:wms_positions(id, full_code)
        )
      `
      )
      .single();

    if (error) throw new Error("No se pudo reclamar la tarea");
    return data as PickTaskWithDetails;
  },

  /**
   * Get pick task detail with items
   */
  async getPickTaskDetail(taskId: string): Promise<PickTaskWithDetails | null> {
    const { data, error } = await supabase
      .from("wms_pick_tasks")
      .select(
        `
        *,
        sales_request:sales_request(
          id,
          client:clients(name),
          delivery_date
        ),
        items:wms_pick_task_items(
          id, product_id, inventory_id, requested_quantity, picked_quantity,
          status, pick_sequence, lot_number, source_position_id,
          scanned_location, scanned_product,
          product:products(id, short_name),
          source_position:wms_positions(id, full_code, barcode)
        )
      `
      )
      .eq("id", taskId)
      .single();

    if (error) return null;
    return data as PickTaskWithDetails;
  },

  /**
   * Start picking (update status to in_progress)
   */
  async startPickTask(taskId: string): Promise<PickTask> {
    const { data, error } = await supabase
      .from("wms_pick_tasks")
      .update({
        status: "in_progress",
        started_at: new Date().toISOString(),
      })
      .eq("id", taskId)
      .select()
      .single();

    if (error) throw new Error("No se pudo iniciar la tarea");
    return data as PickTask;
  },

  /**
   * Pick an item (verify location, update quantity)
   */
  async pickItem(
    itemId: string,
    pickedQuantity: number,
    userId: string
  ): Promise<{ success: boolean }> {
    // Get item details with inventory
    const { data: item, error: itemError } = await supabase
      .from("wms_pick_task_items")
      .select(
        `
        *,
        inventory:wms_inventory(id, box_count, reserved_boxes)
      `
      )
      .eq("id", itemId)
      .single();

    if (itemError || !item) throw new Error("Item no encontrado");

    const typedItem = item as PickTaskItemWithDetails;

    // Validate picked quantity doesn't exceed requested
    if (pickedQuantity > typedItem.requested_quantity) {
      throw new Error(
        `No se puede pickear más de lo solicitado (${typedItem.requested_quantity} cajas)`
      );
    }

    // Update pick task item
    const { error: updateError } = await supabase
      .from("wms_pick_task_items")
      .update({
        picked_quantity: pickedQuantity,
        picked_at: new Date().toISOString(),
        picked_by: userId,
        status:
          pickedQuantity >= typedItem.requested_quantity ? "picked" : "short",
        scanned_location: true,
        scanned_product: true,
      })
      .eq("id", itemId);

    if (updateError) throw new Error("Error al actualizar item");

    // Update inventory (decrement box_count only - reserved_boxes neutralized)
    if (typedItem.inventory) {
      await supabase
        .from("wms_inventory")
        .update({
          box_count: typedItem.inventory.box_count - pickedQuantity,
          // NEUTRALIZED: reserved_boxes no longer decremented
        })
        .eq("id", typedItem.inventory.id);
    }

    // Create movement record
    await supabase.from("wms_movements").insert({
      movement_type: "pick",
      product_id: typedItem.product_id,
      lot_number: typedItem.lot_number,
      box_quantity: pickedQuantity,
      from_position_id: typedItem.source_position_id,
      reference_type: "sales_request",
      reference_id: typedItem.pick_task_id,
      performed_by: userId,
      performed_at: new Date().toISOString(),
    });

    return { success: true };
  },

  /**
   * Complete pick task
   */
  async completePickTask(taskId: string): Promise<PickTask> {
    const { data, error } = await supabase
      .from("wms_pick_tasks")
      .update({
        status: "completed",
        completed_at: new Date().toISOString(),
      })
      .eq("id", taskId)
      .select()
      .single();

    if (error) throw new Error("No se pudo completar la tarea");
    return data as PickTask;
  },

  /**
   * Cancel pick task (release reserved inventory)
   */
  async cancelPickTask(taskId: string): Promise<PickTask> {
    // NEUTRALIZED: reserved_boxes release skipped - feature disabled
    // Reservation release logic removed

    // Update task status
    const { data, error } = await supabase
      .from("wms_pick_tasks")
      .update({
        status: "cancelled",
        completed_at: new Date().toISOString(),
      })
      .eq("id", taskId)
      .select()
      .single();

    if (error) throw new Error("No se pudo cancelar la tarea");
    return data as PickTask;
  },

  /**
   * Get total items and picked count for a task
   */
  async getPickTaskProgress(taskId: string): Promise<{
    total: number;
    picked: number;
    totalQuantity: number;
    pickedQuantity: number;
  }> {
    const { data: items } = await supabase
      .from("wms_pick_task_items")
      .select("status, requested_quantity, picked_quantity")
      .eq("pick_task_id", taskId);

    if (!items)
      return { total: 0, picked: 0, totalQuantity: 0, pickedQuantity: 0 };

    const total = items.length;
    const picked = items.filter(
      (i) => i.status === "picked" || i.status === "short"
    ).length;
    const totalQuantity = items.reduce(
      (sum, i) => sum + (i.requested_quantity || 0),
      0
    );
    const pickedQuantity = items.reduce(
      (sum, i) => sum + (i.picked_quantity || 0),
      0
    );

    return { total, picked, totalQuantity, pickedQuantity };
  },

  // ============================================================================
  // Pre-Invoice Picking Workflow Functions
  // ============================================================================

  /**
   * Get sales requests with status 'por_facturar' grouped by client
   * Includes stock availability check for each item
   */
  async getOrdersReadyForPicking(): Promise<PickingOrdersByClient[]> {
    // Get all por_facturar sales requests with their items
    const { data: salesRequests, error } = await supabase
      .from("sales_request")
      .select(
        `
        id,
        created_at,
        delivery_date,
        total_amount,
        delivery_street,
        delivery_number,
        delivery_city,
        delivery_comuna,
        client:clients(id, name),
        items:sales_request_items(
          id,
          product_id,
          quantity,
          product:products(id, short_name)
        ),
        pick_task:wms_pick_tasks(id, status)
      `
      )
      .eq("status", "por_facturar")
      .order("delivery_date", { ascending: true });

    if (error) throw error;
    if (!salesRequests) return [];

    // Get total WMS inventory for stock availability
    const { data: inventory } = await supabase
      .from("wms_inventory")
      .select("product_id, box_count, reserved_boxes");

    // Build a map of available stock per product
    // NEUTRALIZED: reserved_boxes ignored - available = box_count
    const stockMap = new Map<string, number>();
    if (inventory) {
      for (const inv of inventory) {
        const available = inv.box_count || 0;
        const current = stockMap.get(inv.product_id) || 0;
        stockMap.set(inv.product_id, current + available);
      }
    }

    // Group by client
    const clientMap = new Map<string, PickingOrdersByClient>();

    for (const sr of salesRequests) {
      const client = sr.client as { id: string; name: string } | null;
      if (!client) continue;

      // Check if pick task already exists
      const pickTaskArray = sr.pick_task as
        | { id: string; status: string }[]
        | null;
      const pickTask =
        pickTaskArray && pickTaskArray.length > 0 ? pickTaskArray[0] : null;

      // Build items with stock availability
      const items: PickingOrderItem[] = [];
      let allItemsHaveStock = true;

      const srItems = sr.items as {
        id: string;
        product_id: string;
        quantity: number;
        product: { id: string; short_name: string } | null;
      }[];

      for (const item of srItems || []) {
        const available = stockMap.get(item.product_id) || 0;
        const sufficient = available >= item.quantity;
        if (!sufficient) allItemsHaveStock = false;

        items.push({
          product_id: item.product_id,
          product_name: item.product?.short_name || "Producto",
          quantity: item.quantity,
          stock_available: available,
          sufficient,
        });
      }

      // Get or create client group
      if (!clientMap.has(client.id)) {
        clientMap.set(client.id, {
          client_id: client.id,
          client_name: client.name,
          delivery_address: {
            street: sr.delivery_street || undefined,
            number: sr.delivery_number || undefined,
            city: sr.delivery_city || undefined,
            comuna: sr.delivery_comuna || undefined,
            full_address:
              [
                sr.delivery_street,
                sr.delivery_number,
                sr.delivery_comuna,
                sr.delivery_city,
              ]
                .filter(Boolean)
                .join(", ") || undefined,
          },
          orders: [],
        });
      }

      const clientGroup = clientMap.get(client.id)!;
      clientGroup.orders.push({
        id: sr.id,
        created_at: sr.created_at,
        delivery_date: sr.delivery_date || undefined,
        total_amount: sr.total_amount || 0,
        items_count: items.length,
        stock_available: allItemsHaveStock,
        items,
        pick_task_id: pickTask?.id,
        pick_task_status: pickTask?.status as PickTask["status"] | undefined,
      });
    }

    return Array.from(clientMap.values());
  },

  /**
   * Check if all items in a sales request have sufficient WMS inventory
   */
  async checkStockAvailability(
    salesRequestId: string
  ): Promise<StockAvailabilityResult> {
    // Get sales request items
    const { data: srItems, error: srError } = await supabase
      .from("sales_request_items")
      .select(
        `
        id,
        product_id,
        quantity,
        product:products(id, short_name)
      `
      )
      .eq("sales_request_id", salesRequestId);

    if (srError) throw srError;
    if (!srItems) return { available: true, items: [] };

    // Get WMS inventory
    const { data: inventory } = await supabase
      .from("wms_inventory")
      .select("product_id, box_count, reserved_boxes");

    // Build stock map
    // NEUTRALIZED: reserved_boxes ignored - available = box_count
    const stockMap = new Map<string, number>();
    if (inventory) {
      for (const inv of inventory) {
        const available = inv.box_count || 0;
        const current = stockMap.get(inv.product_id) || 0;
        stockMap.set(inv.product_id, current + available);
      }
    }

    // Check each item
    const items: PickingOrderItem[] = [];
    let allAvailable = true;

    for (const item of srItems) {
      const product = item.product as { id: string; short_name: string } | null;
      const available = stockMap.get(item.product_id) || 0;
      const sufficient = available >= item.quantity;
      if (!sufficient) allAvailable = false;

      items.push({
        product_id: item.product_id,
        product_name: product?.short_name || "Producto",
        quantity: item.quantity,
        stock_available: available,
        sufficient,
      });
    }

    return { available: allAvailable, items };
  },

  /**
   * Create pick task for a por_facturar sales request (before invoice)
   * Reserves inventory using FEFO allocation
   */
  async createPickTaskFromOrder(
    salesRequestId: string,
    userId: string
  ): Promise<PickTask> {
    // Verify sales request exists and is por_facturar
    const { data: sr, error: srError } = await supabase
      .from("sales_request")
      .select(
        `
        id,
        status,
        delivery_date,
        items:sales_request_items(
          id,
          product_id,
          quantity
        )
      `
      )
      .eq("id", salesRequestId)
      .single();

    if (srError || !sr) throw new Error("Pedido no encontrado");
    if (sr.status !== "por_facturar") {
      throw new Error("El pedido debe estar en estado 'Por Facturar'");
    }

    // Check if pick task already exists
    const { data: existingTask } = await supabase
      .from("wms_pick_tasks")
      .select("id")
      .eq("sales_request_id", salesRequestId)
      .maybeSingle();

    if (existingTask) {
      throw new Error("Ya existe una tarea de picking para este pedido");
    }

    // Check stock availability
    const stockCheck = await this.checkStockAvailability(salesRequestId);
    if (!stockCheck.available) {
      throw new Error("No hay suficiente stock para todos los items");
    }

    // Calculate priority based on delivery date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deliveryDate = sr.delivery_date ? new Date(sr.delivery_date) : today;
    deliveryDate.setHours(0, 0, 0, 0);
    const daysDiff = Math.ceil(
      (deliveryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );
    const priority = daysDiff <= 0 ? 1 : daysDiff === 1 ? 2 : 3;

    // Create pick task
    const { data: pickTask, error: taskError } = await supabase
      .from("wms_pick_tasks")
      .insert({
        sales_request_id: salesRequestId,
        status: "assigned",
        priority,
        assigned_to: userId,
        assigned_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (taskError) throw new Error("Error al crear tarea de picking");

    // Allocate inventory using FEFO for each item
    const srItems = sr.items as {
      id: string;
      product_id: string;
      quantity: number;
    }[];
    let pickSequence = 1;

    for (const item of srItems) {
      // Get available inventory for this product sorted by expiration (FEFO)
      const { data: inventoryRows } = await supabase
        .from("wms_inventory")
        .select(
          `
          id,
          position_id,
          product_id,
          lot_number,
          box_count,
          reserved_boxes,
          expiration_date
        `
        )
        .eq("product_id", item.product_id)
        .gt("box_count", 0)
        .order("expiration_date", { ascending: true, nullsFirst: false });

      let remainingQuantity = item.quantity;

      for (const inv of inventoryRows || []) {
        if (remainingQuantity <= 0) break;

        // NEUTRALIZED: reserved_boxes ignored - available = box_count
        const available = inv.box_count;
        if (available <= 0) continue;

        const allocateQty = Math.min(available, remainingQuantity);

        // Create pick task item
        await supabase.from("wms_pick_task_items").insert({
          pick_task_id: pickTask.id,
          product_id: item.product_id,
          inventory_id: inv.id,
          requested_quantity: allocateQty,
          picked_quantity: 0,
          source_position_id: inv.position_id,
          lot_number: inv.lot_number,
          pick_sequence: pickSequence++,
          status: "pending",
          scanned_location: false,
          scanned_product: false,
        });

        // NEUTRALIZED: reserved_boxes no longer updated
        // await supabase
        //   .from("wms_inventory")
        //   .update({
        //     reserved_boxes: inv.reserved_boxes + allocateQty,
        //   })
        //   .eq("id", inv.id);

        remainingQuantity -= allocateQty;
      }
    }

    return pickTask as PickTask;
  },

  /**
   * Generate invoice after picking is complete
   * Calls create-factura edge function
   */
  async generateInvoiceAfterPicking(
    pickTaskId: string
  ): Promise<{ success: boolean; error?: string }> {
    // Get pick task with sales request
    const { data: pickTask, error: taskError } = await supabase
      .from("wms_pick_tasks")
      .select("id, status, sales_request_id")
      .eq("id", pickTaskId)
      .single();

    if (taskError || !pickTask) {
      return { success: false, error: "Tarea de picking no encontrada" };
    }

    if (pickTask.status !== "completed") {
      return {
        success: false,
        error: "La tarea de picking debe estar completada",
      };
    }

    // Call create-factura-picking edge function. It enforces the finished-goods
    // stock gate server-side (409 INSUFFICIENT_STOCK) before emitting in BSale.
    const { data, error } = await supabase.functions.invoke(
      "create-factura-picking",
      {
        body: {
          salesRequestId: pickTask.sales_request_id,
          pickTaskId: pickTaskId,
        },
      }
    );

    if (error) {
      // functions.invoke returns a FunctionsHttpError on non-2xx; the JSON body
      // (with the INSUFFICIENT_STOCK / BSale message) lives on error.context,
      // a Response that must be awaited — reading its props directly yields
      // undefined, which is why failures used to collapse to the generic text.
      let errorMessage = "Error al generar factura";
      const context = (error as { context?: Response }).context;
      if (context && typeof context.json === "function") {
        const body = await context.json().catch(() => null);
        if (body?.error) errorMessage = body.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return { success: false, error: errorMessage };
    }

    return { success: true };
  },

  /**
   * Get all active bodega users available for task assignment
   */
  async getAssignableBodegaUsers(): Promise<
    { id: string; name: string; lastname: string }[]
  > {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, name, lastname")
      .in("role", ["bodega", "superadmin"])
      .eq("is_active", true)
      .order("name");

    if (error) throw new Error("Error al obtener usuarios de bodega");
    return data ?? [];
  },

  /**
   * Assign (or reassign) a pick task to a bodega user
   */
  async assignPickTask(taskId: string, assigneeId: string): Promise<void> {
    const { error } = await supabase
      .from("wms_pick_tasks")
      .update({
        assigned_to: assigneeId,
        assigned_at: new Date().toISOString(),
        status: "assigned",
      })
      .eq("id", taskId);

    if (error) throw new Error("Error al asignar tarea de picking");
  },

  /**
   * Bulk assign multiple pick tasks to a bodega user
   */
  async assignPickTasksBulk(
    taskIds: string[],
    assigneeId: string
  ): Promise<{ successCount: number; errorCount: number }> {
    let successCount = 0;
    let errorCount = 0;

    for (const taskId of taskIds) {
      try {
        const { error } = await supabase
          .from("wms_pick_tasks")
          .update({
            assigned_to: assigneeId,
            assigned_at: new Date().toISOString(),
            status: "assigned",
          })
          .eq("id", taskId);

        if (error) throw error;
        successCount++;
      } catch {
        errorCount++;
      }
    }

    return { successCount, errorCount };
  },

  /**
   * Get all pick tasks for the assignment panel (managers only)
   * Returns tasks that are pending or assigned (not completed/cancelled)
   */
  async getPickTasksForAssignment(): Promise<PickTaskWithDetails[]> {
    const { data, error } = await supabase
      .from("wms_pick_tasks")
      .select(
        `
        *,
        sales_request:sales_request_id!inner(
          id,
          status,
          client:client_id(name),
          delivery_date,
          price_with_iva,
          delivery_street,
          delivery_number,
          delivery_complement,
          delivery_comuna,
          delivery_city
        ),
        assignee:assigned_to(name, lastname),
        items:wms_pick_task_items(
          id,
          requested_quantity,
          product:product_id(short_name)
        )
      `
      )
      .in("status", ["pending", "assigned", "in_progress"])
      .eq("sales_request.status", "por_facturar")
      .order("created_at", { ascending: false });

    if (error) throw new Error("Error al obtener tareas de picking");
    return (data as PickTaskWithDetails[]) ?? [];
  },
};

export default pickingService;
